import { RegisterFormData } from "./pages/Register";
import { SignInFormData } from "./pages/SignIn";
import {
  ContactFormData,
  EnquiryFormData,
  CallBack,
  VideoConsultation,
  HotelSearchResponse,
  HotelType,
  PaymentIntentResponse,
  UserType,
  BlogType,
  SubscribeFormData,
  EnquireRoomFormData,
  EbookFormData,
} from "../../backend/src/shared/types";
import { SocialBooking, VoucherClaimedType } from "../../backend/src/models/socialBookings";
import { BookingResponse, PassengerDetails } from "../../backend/src/models/bookingDetails";
import { Booking, BookingToTable } from "./pages/MyBookings";
import { Passenger } from "../../backend/src/models/bookingGuestDetails";
import { AutoCompleteResponse } from "../../backend/src/models/rateHawkModels/autoCompleteModel";
import { HotelPageResponse } from "../../backend/src/models/rateHawkModels/hotelPageResponseModel";
import { HotelDataInfo } from "../../backend/src/models/rateHawkModels/hotelDataInfo";
import { PreBookHotelResponse } from "../../backend/src/models/rateHawkModels/preBookHotelResponseModel";
import { OrderBookingFormResponse } from "../../backend/src/models/rateHawkModels/orderBookingFormResponseModel";
// import { BookingFormData } from "./forms/BookingForm/BookingForm";

export type ForgotPasswordFormData = {
  email: string;
};

export type ResetPasswordFormData = {
  password: string;
};

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

export const fetchCurrentUser = async (): Promise<UserType> => {
  const response = await fetch(`${API_BASE_URL}/api/users/me`, {
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error("Error fetching user");
  }
  return response.json();
};

export const register = async (formData: RegisterFormData) => {
  const response = await fetch(`${API_BASE_URL}/api/users/register`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(formData),
  });

  if (!response.ok) {
    const responseBody = await response.json();
    throw new Error(responseBody.message);
  }
};

export const signIn = async (formData: SignInFormData) => {
  const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(formData),
  });

  const body = await response.json();

  if (!response.ok) {
    throw new Error(body.message || "Something went wrong");
  }

  return body;
};

export const validateToken = async () => {
  const response = await fetch(`${API_BASE_URL}/api/auth/validate-token`, {
    credentials: "include",
  });

  const body = await response.json();

  if (!response.ok) {
    throw new Error(body.message || "Token validation failed");
  }

  return body;
};

export const signOut = async () => {
  const response = await fetch(`${API_BASE_URL}/api/auth/logout`, {
    credentials: "include",
    method: "POST",
  });

  if (!response.ok) {
    throw new Error("Error during sign out");
  }
};

export const forgotPassword = async (formData: ForgotPasswordFormData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/users/forgot-password`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
    });

    if (!response.ok) {
      const responseBody = await response.json();
      throw new Error(responseBody.message);
    }

    // If you want to return some data on success (e.g., a success message):
    return await response.json(); // Assuming the server returns a success message or relevant data

  } catch (error: any) {
    // Handling network errors or unexpected issues
    throw new Error(error.message || "Something went wrong. Please try again.");
  }
};

export const resetPassword = async (token: string, password: string) => {
  const response = await fetch(`${API_BASE_URL}/api/users/reset-password/${token}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ password }),
  });

  if (!response.ok) {
    const responseBody = await response.json();
    throw new Error(responseBody.message);
  }
};

export const addMyHotel = async (hotelFormData: FormData) => {
  const response = await fetch(`${API_BASE_URL}/api/my-hotels`, {
    method: "POST",
    credentials: "include",
    body: hotelFormData,
  });

  if (!response.ok) {
    throw new Error("Failed to add hotel");
  }

  return response.json();
};

export const fetchMyHotels = async (): Promise<HotelType[]> => {
  const response = await fetch(`${API_BASE_URL}/api/my-hotels`, {
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Error fetching hotels");
  }

  return response.json();
};

export const fetchMyHotelById = async (hotelId: string): Promise<HotelType> => {
  const response = await fetch(`${API_BASE_URL}/api/my-hotels/${hotelId}`, {
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Error fetching Hotels");
  }

  return response.json();
};

export const updateMyHotelById = async (hotelFormData: FormData) => {
  const response = await fetch(
    `${API_BASE_URL}/api/my-hotels/${hotelFormData.get("hotelId")}`,
    {
      method: "PUT",
      body: hotelFormData,
      credentials: "include",
    }
  );

  if (!response.ok) {
    throw new Error("Failed to update Hotel");
  }

  return response.json();
};

export type SearchParams = {
  destination?: string;
  checkIn?: string;
  checkOut?: string;
  adultCount?: string;
  childCount?: string;
  page?: string;
  facilities?: string[];
  types?: string[];
  stars?: string[];
  maxPrice?: string;
  sortOption?: string;
};

export const searchHotels = async (
  searchParams: SearchParams
): Promise<HotelSearchResponse> => {
  const queryParams = new URLSearchParams();
  queryParams.append("destination", searchParams.destination || "");
  queryParams.append("checkIn", searchParams.checkIn || "");
  queryParams.append("checkOut", searchParams.checkOut || "");
  queryParams.append("adultCount", searchParams.adultCount || "");
  queryParams.append("childCount", searchParams.childCount || "");
  queryParams.append("page", searchParams.page || "");

  queryParams.append("maxPrice", searchParams.maxPrice || "");
  queryParams.append("sortOption", searchParams.sortOption || "");

  searchParams.facilities?.forEach((facility) =>
    queryParams.append("facilities", facility)
  );

  searchParams.types?.forEach((type) => queryParams.append("types", type));
  searchParams.stars?.forEach((star) => queryParams.append("stars", star));

  const response = await fetch(
    `${API_BASE_URL}/api/hotels/search?${queryParams}`
  );

  if (!response.ok) {
    throw new Error("Error fetching hotels");
  }

  return response.json();
};

export const fetchHotels = async (): Promise<HotelType[]> => {
  const response = await fetch(`${API_BASE_URL}/api/hotels`);
  if (!response.ok) {
    throw new Error("Error fetching hotels");
  }
  return response.json();
};

export const fetchHotelBySlug = async (slug: string): Promise<HotelType> => {
  const response = await fetch(`${API_BASE_URL}/api/hotels/${slug}`);
  if (!response.ok) {
    throw new Error("Error fetching Hotel");
  }

  return response.json();
};

export const createPaymentIntents = async (
  hotelId: string,
  numberOfNights: string
): Promise<PaymentIntentResponse> => {
  const response = await fetch(
    `${API_BASE_URL}/api/hotels/${hotelId}/bookings/payment-intent`,
    {
      credentials: "include",
      method: "POST",
      body: JSON.stringify({ numberOfNights }),
      headers: {
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error("Error fetching payment intent");
  }

  return response.json();
};

// export const createRoomBooking = async (formData: BookingFormData) => {
//   const response = await fetch(
//     `${API_BASE_URL}/api/hotels/${formData.hotelId}/bookings`,
//     {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//       },
//       credentials: "include",
//       body: JSON.stringify(formData),
//     }
//   );

//   if (!response.ok) {
//     throw new Error("Error booking room");
//   }
// };

export const fetchMyBookings = async (): Promise<HotelType[]> => {
  const response = await fetch(`${API_BASE_URL}/api/my-bookings`, {
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Unable to fetch bookings");
  }

  return response.json();
};

// services/emailService.ts
export const sendEmail = async (formData: ContactFormData): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/email/sendEmail`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(formData),
  });

  if (!response.ok) {
    const errorData = await response.json();
    console.error('Error sending message:', errorData);
    throw new Error('Failed to send message');
  }
};

export const sendEnquiryEmail = async (formData: EnquiryFormData): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/enquiry-email/sendEmail`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(formData),
  });

  if (!response.ok) {
    const errorData = await response.json();
    console.error('Error sending message:', errorData);
    throw new Error('Failed to send message');
  }
};

export const sendCallBackEmail = async (formData: CallBack): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/callback-email/sendEmail`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(formData),
  });

  if (!response.ok) {
    const errorData = await response.json();
    console.error('Error sending message:', errorData);
    throw new Error('Failed to send message');
  }
};

export const sendVideoConsultationEmail = async (formData: VideoConsultation): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/video-booking/sendEmail`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(formData),
  });

  if (!response.ok) {
    const errorData = await response.json();
    console.error('Error sending message:', errorData);
    throw new Error('Failed to send message');
  }
};

export const sendRoomEnquiryEmail = async (formData: EnquireRoomFormData): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/enquire-room-email/sendEmail`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(formData),
  });

  if (!response.ok) {
    const errorData = await response.json();
    console.error('Error sending message:', errorData);
    throw new Error('Failed to send message');
  }
};

export const sendSubscribeEmail = async (formData: SubscribeFormData): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/subscribe-email/sendEmail`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(formData),
  });

  if (!response.ok) {
    const errorData = await response.json();
    console.error('Error sending message:', errorData);
    throw new Error('Failed to send message');
  }
};

export const sendEbookEmail = async (formData: EbookFormData): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/ebook-email/sendEmail`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(formData),
  });

  if (!response.ok) {
    const errorData = await response.json();
    console.error('Error sending message:', errorData);
    throw new Error('Failed to send message');
  }
};

// Blog related API functions
export const addBlog = async (blogFormData: FormData) => {
  const response = await fetch(`${API_BASE_URL}/api/blogs`, {
    method: "POST",
    credentials: "include",
    body: blogFormData,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to add blog");
  }

  return response.json();
};

export const fetchMyBlogs = async (): Promise<BlogType[]> => {
  const response = await fetch(`${API_BASE_URL}/api/blogs`, {
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Error fetching blogs");
  }

  return response.json();
};

export const fetchBlogBySlug = async (slug: string): Promise<BlogType> => {
  const response = await fetch(`${API_BASE_URL}/api/blogs/${slug}`, {
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Error fetching blog");
  }

  return response.json();
};

export const deleteBlog = async (slug: string): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/blogs/${slug}`, {
    method: "DELETE",
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Failed to delete blog");
  }

  return response.json();
};


export const fetchBlogById = async (blogId: string): Promise<BlogType> => {
  const response = await fetch(`${API_BASE_URL}/api/blogs/${blogId}`, {
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Error fetching blog");
  }

  return response.json();
};

export const updateBlogById = async (blogFormData: FormData) => {
  const slug = blogFormData.get("slug");
  if (!slug) {
    throw new Error("Blog slug is missing");
  }

  const response = await fetch(`${API_BASE_URL}/api/blogs/${slug}`, {
    method: "PUT",
    credentials: "include",
    body: blogFormData,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to update blog");
  }

  return response.json();
};

export const fetchBlogs = async (): Promise<BlogType[]> => {
  const response = await fetch(`${API_BASE_URL}/api/blogs`, {
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error("Error fetching blogs");
  }
  return response.json();
};

export const searchSerpHotels = async (query: string, checkInDate: string, checkOutDate: string, adults: { toString: () => string; }, currency: string, country: string, language: string, propertyToken: string) => {
  try {
    const url = new URL(`${API_BASE_URL}/api/serp-hotels/search`, window.location.origin);
    url.searchParams.append('query', query);
    url.searchParams.append('checkInDate', checkInDate);
    url.searchParams.append('checkOutDate', checkOutDate);
    url.searchParams.append('adults', adults.toString());
    url.searchParams.append('currency', currency);
    url.searchParams.append('country', country);
    url.searchParams.append('language', language);
    url.searchParams.append('propertyToken', propertyToken);

    const response = await fetch(url.toString());
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching data from backend:', error);
    return null;
  }
};

export const fetchCountries = async () => {
  const response = await fetch(`${API_BASE_URL}/api/hotelBedsRoute/countries`, {
    credentials: "include",
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const responseText = await response.text();
    console.error(responseText);
    throw new Error(`Failed to fetch countries: ${response.status}`);
  }

  try {
    return await response.json();
  } catch (error) {
    console.error('Failed to parse JSON response');
    throw new Error('Failed to parse JSON response');
  }
};

export const fetchHotelsList = async (countryCode: string) => {
  const response = await fetch(`${API_BASE_URL}/api/hotelBedsRoute/hotelsList?countryCode=${countryCode}`, {
    credentials: "include",
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const responseText = await response.text();
    console.error(responseText);
    throw new Error(`Failed to fetch hotels: ${response.status}`);
  }

  try {
    return await response.json();
  } catch (error) {
    console.error('Failed to parse JSON response');
    throw new Error('Failed to parse JSON response');
  }
};

export const fetchHotelDetailsHB = async (hotelCode: string) => {
  const response = await fetch(`${API_BASE_URL}/api/hotelBedsRoute/hotelDetails?hotelCode=${hotelCode}`, {
    credentials: "include",
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const responseText = await response.text();
    console.error(responseText);
    throw new Error(`Failed to fetch hotels: ${response.status}`);
  }

  try {
    return await response.json();
  } catch (error) {
    console.error('Failed to parse JSON response');
    throw new Error('Failed to parse JSON response');
  }
};

export const postHotelAvailability = async (availabilityRequest: {
  stay: {
    checkIn: string;
    checkOut: string;
  };
  occupancies: {
    rooms: number;
    adults: number;
    children: number;
    paxes?: { type: string; age: number }[];
  }[];
  hotelCodes: number[];
}) => {
  const response = await fetch(`${API_BASE_URL}/api/hotelBedsRoute/hotelAvailability`, {
    credentials: "include",
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(availabilityRequest),
  });

  if (!response.ok) {
    const responseText = await response.text();
    console.error(responseText);
    throw new Error(`Failed to check hotel availability: ${response.status}`);
  }

  try {
    return await response.json();
  } catch (error) {
    console.error('Failed to parse JSON response');
    throw new Error('Failed to parse JSON response');
  }
};

//Humming Bird
export const fetchDestinations = async () => {
  const response = await fetch(`${API_BASE_URL}/api/hbtRoute/destinations`, {
    credentials: "include",
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const responseText = await response.text();
    console.error(responseText);
    throw new Error(`Failed to fetch destinations: ${response.status}`);
  }

  try {
    return await response.json();
  } catch (error) {
    console.error('Failed to parse JSON response');
    throw new Error('Failed to parse JSON response');
  }
};

export const fetchProperties = async (destinationCode: string) => {
  const response = await fetch(`${API_BASE_URL}/api/hbtRoute/propertyList?destinationCode=${destinationCode}`, {
    credentials: "include",
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const responseText = await response.text();
    console.error(responseText);
    throw new Error(`Failed to fetch hotels: ${response.status}`);
  }

  try {
    return await response.json();
  } catch (error) {
    console.error('Failed to parse JSON response');
    throw new Error('Failed to parse JSON response');
  }
};

export const fetchPropertyDetails = async (destinationCode: string, hotelCode: string) => {
  const response = await fetch(`${API_BASE_URL}/api/hbtRoute/propertyDetails?destinationCode=${destinationCode}&hotelCode=${hotelCode}`, {
    credentials: "include",
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const responseText = await response.text();
    console.error(responseText);
    throw new Error(`Failed to fetch hotels: ${response.status}`);
  }

  try {
    return await response.json();
  } catch (error) {
    console.error('Failed to parse JSON response');
    throw new Error('Failed to parse JSON response');
  }
};

export const getResponseToken = async (tokenRequest : {
  source: {
    userEmail: string;
    requestRef: string;
  },
  requestCriteria: {
    region: string;
    destination: {
      destinationCode: string;
      locationCode: string;
    };
    hotelCodes: string[];
    stayPeriod: {
      checkInDate: string;
      checkOutDate: string;
    };
    nationalityCode: string;
    roomRequests: {
      adults: number;
      children: number;
      childAges: number[];
    }[];
    mealTypeCode: string;
    vacationTypeCode: string;
    preferences: {
      availability: string;
    };
  }
}) => {
  const response = await fetch(`${API_BASE_URL}/api/hbtRoute/propertToken`, {
    credentials: "include",
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(tokenRequest),
  });

  if (!response.ok) {
    const responseText = await response.text();
    console.error(responseText);
    throw new Error(`Failed to check hotel availability: ${response.status}`);
  }

  try {
    return await response.json();
  } catch (error) {
    console.error('Failed to parse JSON response');
    throw new Error('Failed to parse JSON response');
  }
};

export const fetchPropertyRoomDetails = async (searchResponseToken: string,) => {
  const response = await fetch(`${API_BASE_URL}/api/hbtRoute/propertyRoomDetails?searchResponseToken=${searchResponseToken}`, {
    credentials: "include",
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const responseText = await response.text();
    console.error(responseText);
    throw new Error(`Failed to fetch hotels: ${response.status}`);
  }

  try {
    return await response.json();
  } catch (error) {
    console.error('Failed to parse JSON response');
    throw new Error('Failed to parse JSON response');
  }
};

export const createBooking = async (
  hotelCode: string,
  searchResponseToken: string,
  bookingRequest: {
    primaryGuestName: string;
    groups: {
      id: string;
      room: string;
      deal: string;
    }[];
  },
  remarks: string
) => {
  const response = await fetch(`${API_BASE_URL}/api/hbtRoute/booking/${hotelCode}/create/${searchResponseToken}`, {
    credentials: "include",
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      bookingRequest: bookingRequest,
      remarks: remarks,
    }),
  });

  if (!response.ok) {
    const responseText = await response.text();
    console.error(responseText);
    throw new Error(`Failed to create booking: ${response.status}`);
  }

  try {
    return await response.json();
  } catch (error) {
    console.error('Failed to parse JSON response');
    throw new Error('Failed to parse JSON response');
  }
};

export const getBookingDetails = async (bookingResponseToken: string) => {
  const response = await fetch(`${API_BASE_URL}/api/hbtRoute/bookingDetails?bookingResponseToken=${bookingResponseToken}`, {
    credentials: "include",
    method: "GET",
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    // const responseText = await response.text();
    // console.log(responseText);
    throw new Error(`Failed to fetch hotels: ${response.status}`);
  }

  try {
    return await response.json();
  } catch (error) {
    console.error('Failed to parse Json response');
    throw new Error('Failed to parse Json response');
  }
};

export const saveBookingToDatabase = async (bookingDetails: BookingResponse, userId: string, holdOrPaid: string, createdAt: string, email: string ) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/bookingRoute/createBooking`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        userId: userId,
        responseToken: bookingDetails.responseToken,
        reference: bookingDetails.reference,
        subject: bookingDetails.subject,
        tripStartDate: bookingDetails.tripStartDate,
        tripEndDate: bookingDetails.tripEndDate,
        bookedDate: bookingDetails.bookedDate,
        holidayType: bookingDetails.holidayType,
        primaryGuestName: bookingDetails.primaryGuestName,
        status: bookingDetails.status,
        bookingStatus: bookingDetails.bookingStatus,
        remarks: bookingDetails.remarks,
        grandTotal: bookingDetails.grandTotal,
        holdOrPaid: holdOrPaid,
        createdAt: createdAt,
        email: email,
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to save booking to database");
    }

    // const result = await response.json();
    // console.log(result.message);
  } catch (error) {
    console.error("Error saving booking to database:", error);
  }
};

export const updateBookingInDatabase = async (
  bookingDetails: BookingResponse,
  userId: string,
  //holdOrPaid: string,
  updatedAt: string
) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/bookingRoute/updateBookingByReference/${bookingDetails.reference}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        userId: userId,
        responseToken: bookingDetails.responseToken,
        reference: bookingDetails.reference,
        subject: bookingDetails.subject,
        tripStartDate: bookingDetails.tripStartDate,
        tripEndDate: bookingDetails.tripEndDate,
        bookedDate: bookingDetails.bookedDate,
        holidayType: bookingDetails.holidayType,
        primaryGuestName: bookingDetails.primaryGuestName,
        status: bookingDetails.status,
        bookingStatus: bookingDetails.bookingStatus,
        remarks: bookingDetails.remarks,
        grandTotal: bookingDetails.grandTotal,
        //holdOrPaid: holdOrPaid,
        updatedAt: updatedAt,
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to update booking in the database");
    }

    // const result = await response.json();
    // console.log(result.message);
  } catch (error) {
    console.error("Error updating booking in the database:", error);
  }
};


export const fetchUserBookings = async (userId: string, isAdmin: Boolean): Promise<Booking[]> => {
  try {
      const response = await fetch(`${API_BASE_URL}/api/bookingRoute/getBookings?userId=${userId}&isAdmin=${isAdmin}`, {
          method: "GET",
          headers: {
              "Content-Type": "application/json",
          },
      });

      if (!response.ok) {
          throw new Error("Failed to fetch bookings");
      }

      const bookings = await response.json();
      //console.log("User Bookings:", bookings);
      return bookings; 
  } catch (error) {
      console.error("Error fetching user bookings:", error);
      return []; 
  }
};

export const fetchBookingByReference = async (reference: string): Promise<Booking | null> => {
  try {
      const response = await fetch(`${API_BASE_URL}/api/bookingRoute/getBookingByReference?reference=${reference}`, {
          method: "GET",
          headers: {
              "Content-Type": "application/json",
          },
      });

      if (!response.ok) {
          throw new Error("Failed to fetch booking by reference");
      }

      const booking = await response.json();
      return booking; 
  } catch (error) {
      console.error("Error fetching booking by reference:", error);
      return null; 
  }
};

export const saveGuestDetailsToDatabase = async (
  userId: string,
  passengers: Passenger[],
  bookingResponseToken: string,
) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/bookingRoute/createGuestDetails`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        userId,
        passengers,
        bookingResponseToken
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to save guest details to database");
    }

    //const result = await response.json();
    //console.log("Guest Details Saved:", result.message);
  } catch (error) {
    console.error("Error saving guest details to database:", error);
  }
};

export const getGuestDetailsFromDatabase = async (
  userId: string,
  bookingResponseToken: string,
) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/bookingRoute/getGuestDetails?userId=${userId}&bookingResponseToken=${bookingResponseToken}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Failed to get guest details");
    }

    const guestDetails = await response.json();
    //console.log("Guest Details:", guestDetails);
    return guestDetails;
  } catch (error) {
    console.error("Error fetching guest details:", error);
    return []; 
  }
}

export const cancelBooking = async (bookingResponseToken: string, reference: string) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/hbtRoute/cancelBooking`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        bookingResponseToken,
        reference,
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to cancel booking");
    }

    //const result = await response.json();
    //console.log(result.message);
  } catch (error) {
    console.error("Error cancelling a booking:", error);
  }
};

export const updateGuestDetails = async (bookingResponseToken: string, guests: PassengerDetails[]) => {
  try {
      const response = await fetch(`${API_BASE_URL}/api/hbtRoute/updateGuestDetails`, {
          method: "POST",
          headers: {
              "Content-Type": "application/json",
          },
          body: JSON.stringify({
              bookingResponseToken,
              details: guests,
          }),
      });

      if (!response.ok) {
          throw new Error("Failed to Update guest details");
      }

      //const result = await response.json();
      //console.log(result.message);
  } catch (error) {
      console.error("Error updating guest details:", error);
  }
};


export const createPaymentIntent = async (amount: number, currency: string, email: string) => {
  try {
    // Fetch the client secret from your backend
    const response = await fetch(`${API_BASE_URL}/api/create-payment-intent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount, currency, email }), // Ensure email is included
    });

    const data = await response.json();
    return data.clientSecret;
  } catch (error) {
    console.error('Error creating payment intent:', error);
    throw new Error('Failed to create payment intent.');
  }
};

export const confirmCardPayment = async (stripe: any, clientSecret: string, cardElement: any) => {
  try {
    const result = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: cardElement,
      },
    });

    if (result.error) {
      throw new Error(result.error.message);
    } else if (result.paymentIntent?.status === 'succeeded') {
      return 'Payment succeeded!';
    }

    throw new Error('Payment failed.');
  } catch (error) {
    console.error('Error confirming card payment:', error);
    throw new Error('Failed to confirm card payment.');
  }
};

export const updateBookingStatus = async (holdOrPaid: string, reference: string, paidAmount: string) => {
  if (!reference) return;
  
  try {
      const response = await fetch(`${API_BASE_URL}/api/bookingRoute/updateBookingStatus`, {
          method: "PATCH",
          headers: {
              "Content-Type": "application/json",
          },
          body: JSON.stringify({
              reference: reference,
              holdOrPaid,
              paidAmount
          }),
      });
      
      const data = await response.json();
      if (response.ok) {
          //console.log(`Booking status updated to ${holdOrPaid}`);
      } else {
          console.error("Failed to update booking status:", data.message);
      }
  } catch (error) {
      console.error("Error updating booking status:", error);
  }
};

export const updateStatus = async (reference: string, newStatus: string) => {
  try {
      const response = await fetch(`${API_BASE_URL}/api/bookingRoute/updateStatus`, {
          method: "PATCH",
          headers: {
              "Content-Type": "application/json",
          },
          body: JSON.stringify({ reference, holdOrPaid: newStatus }),
      });

      if (!response.ok) {
          throw new Error("Failed to update booking status");
      }

      //console.log(`Status updated to "${newStatus}" for reference: ${reference}`);
      return true;
  } catch (error) {
      console.error("Error updating booking status:", error);
      return false;
  }
};

export const sendConfirmBookingEmail = async (email: string, booking: BookingToTable, updatedStatus: string): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/bookingConfirmed-email/sendEmail`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({email, booking, updatedStatus}),
  });

  if (!response.ok) {
    const errorData = await response.json();
    console.error('Error sending message:', errorData);
    throw new Error('Failed to send message');
  }
};

export const sendDepositConfirmationEmail = async (email: string, booking: BookingToTable, updatedStatus: string, paidAmount: string): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/depositConfirmed-email/sendEmail`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({email, booking, updatedStatus, paidAmount}),
  });

  if (!response.ok) {
    const errorData = await response.json();
    console.error('Error sending message:', errorData);
    throw new Error('Failed to send message');
  }
};

export const sendCancelBookingEmail = async (email: string, booking: BookingToTable, updatedStatus: string): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/bookingCancelled-email/sendEmail`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({email, booking, updatedStatus}),
  });

  if (!response.ok) {
    const errorData = await response.json();
    console.error('Error sending cancellation message:', errorData);
    throw new Error('Failed to send cancellation message');
  }
};

export const sendHoldBookingEmail = async (email: string, booking: BookingToTable, updatedStatus: string): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/holdBooking-email/sendEmail`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({email, booking, updatedStatus}),
  });

  if (!response.ok) {
    const errorData = await response.json();
    console.error('Error sending hold message:', errorData);
    throw new Error('Failed to send cancellation message');
  }
};

export const sendSocialBookingEmail = async (formData: SocialBooking): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/social-booking-email/sendEmail`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(formData),
  });

  if (!response.ok) {
    const errorData = await response.json();
    console.error('Error sending message:', errorData);
    throw new Error('Failed to send message');
  }
};

export const sendVoucherClaimedEmail = async (formData: VoucherClaimedType): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/social-booking-email/sendVoucherEmail`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(formData),
  });

  if (!response.ok) {
    const errorData = await response.json();
    console.error('Error sending message:', errorData);
    throw new Error('Failed to send message');
  }
};

export const sendUserRegisteredEmail = async (name: string, email: string): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/userRegistered-email/sendEmail`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ name, email }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    console.error('Error sending message:', errorData);
    throw new Error('Failed to send message');
  }
};

export const sendWelcomeEmail = async (name: string, email: string): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/welcome-email/sendEmail`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ name, email }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    console.error('Error sending message:', errorData);
    throw new Error('Failed to send message');
  }
};

export const getUsdToGbpExchangeRate = async (): Promise<number> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/exchangeRoute/convert-usd-to-gbp`);

    if (!response.ok) {
      throw new Error(`Failed to fetch exchange rate: ${response.statusText}`);
    }

    // Log the response body to inspect its contents
    const textResponse = await response.text();
    console.log("Response Text:", textResponse);

    const data = JSON.parse(textResponse); // Parse it explicitly as JSON
    console.log("Backend Response:", data);

    if (data.rate !== undefined) {
      return data.rate; // Return the rate from the backend response
    }

    throw new Error("Rate not found in the backend response.");
  } catch (error) {
    console.error("Error fetching USD to GBP exchange rate:", error);
    throw error;
  }
};

export const saveSocialBookingToDatabase = async (
  socialBookingDetails: {
    firstName: string;
    lastName: string;
    contactNumber?: string;
    email: string;
    departureCountry: string;
    arrivalCountry: string;
    travelers: string;
    adults: string;
    children: string;
    message: string;
    voucherCode?: string;
  },
  createdAt: string
) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/socialBooking/createSocialBooking`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        firstName: socialBookingDetails.firstName,
        lastName: socialBookingDetails.lastName,
        contactNumber: socialBookingDetails.contactNumber,
        email: socialBookingDetails.email,
        departureCountry: socialBookingDetails.departureCountry,
        arrivalCountry: socialBookingDetails.arrivalCountry,
        travelers: socialBookingDetails.travelers,
        adults: socialBookingDetails.adults,
        children: socialBookingDetails.children,
        message: socialBookingDetails.message,
        voucherCode: socialBookingDetails.voucherCode,
        createdAt: createdAt,
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to save social booking to database");
    }

  } catch (error) {
    console.error("Error saving social booking to database:", error);
  }
};

export const createVoucherClaimed = async (formData: {
  firstName: string;
  lastName: string;
  contactNumber: string;
  email: string;
  voucherCode: string;
}) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/socialBooking/createVoucherClaimed`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
    });

    if (!response.ok) {
      throw new Error("Failed to save voucher claimed to database");
    }

  } catch (error) {
    console.error("Error saving voucher claimed to database:", error);
  }
};

export const fetchVoucherClaimed = async (): Promise<VoucherClaimedType[]> => {
  const response = await fetch(`${API_BASE_URL}/api/socialBooking/claimedVouchers`, {
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Error fetching voucher claimed");
  }

  return response.json();
};


//RateHawk API functions
export const fetchAutocompleteSuggestions = async (query: string, language: string): Promise<AutoCompleteResponse> => {
  const body = JSON.stringify({ query, language });
  try {
    const response = await fetch(`${API_BASE_URL}/api/rateHawkRoute/autocomplete`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: body,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch autocomplete suggestions: ${response.statusText}. Details: ${errorText}`);
    }

    const suggestions: AutoCompleteResponse = await response.json();
    return suggestions;
  } catch (error) {
    console.error("Error fetching autocomplete suggestions:", error);
    throw error;
  }
};

export const fetchRegionSearchResults = async (params: {
  checkin: string;
  checkout: string;
  residency: string;
  language: string;
  guests: { adults: number; children: number[] }[];
  region_id: number;
  currency: string;
}): Promise<number[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/rateHawkRoute/region-search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch region search results: ${response.statusText}. Details: ${errorText}`);
    }

    const hotelIds: number[] = await response.json();
    return hotelIds;
  } catch (error) {
    console.error('Error fetching region search results:', error);
    throw error;
  }
};


export const fetchFilteredHotels = async (
  params: {
    checkin: string;
    checkout: string;
    residency: string;
    language: string;
    guests: { adults: number; children: number[] }[];
    region_id: number;
    currency: string;
  },
  page: number,
  limit: number = 20
): Promise<{ hotels: any[]; totalCount: number }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/rateHawkRoute/hotelDumpData`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...params, page, limit }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch filtered hotels: ${response.statusText}. Details: ${errorText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching filtered hotels:', error);
    throw error;
  }
};

export const fetchHotelPage = async (params: {
  checkin: string;
  checkout: string;
  residency: string;
  language: string;
  guests: { adults: number; children: number[] }[];
  id: string;
  currency: string;
}): Promise<HotelPageResponse | null> => {
  const body = JSON.stringify(params);

  try {
    const response = await fetch(`${API_BASE_URL}/api/rateHawkRoute/hotelPage`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: body,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch hotel details: ${response.statusText}. Details: ${errorText}`);
    }

    const data: HotelPageResponse = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching hotel details:", error);
    throw error;
  }
};

export const fetchHotelDataInfo = async (params: {
  id: string;
  language: string;
}): Promise<HotelDataInfo | null> => {
  const body = JSON.stringify(params);

  try {
    const response = await fetch(`${API_BASE_URL}/api/rateHawkRoute/hotelDataInfo`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: body,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch hotel data info: ${response.statusText}. Details: ${errorText}`);
    }

    const data: HotelDataInfo = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching hotel data info:", error);
    throw error;
  }
};

export const preBookHotel = async (params: {
  hash: string;
  price_increase_percent: number;
}): Promise<PreBookHotelResponse | null> => {
  const body = JSON.stringify(params);

  try {
    const response = await fetch(`${API_BASE_URL}/api/rateHawkRoute/preBook`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: body,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to pre-book hotel: ${response.statusText}. Details: ${errorText}`);
    }

    const data: PreBookHotelResponse = await response.json();
    return data;
  } catch (error) {
    console.error("Error pre-booking hotel:", error);
    throw error;
  }
};

export const orderBookingForm = async (params: {
  partner_order_id: string;
  book_hash: string;
  language: string;
  user_ip: string;
}): Promise<OrderBookingFormResponse | null> => {
  const body = JSON.stringify(params);

  try {
    const response = await fetch(`${API_BASE_URL}/api/rateHawkRoute/orderBookingForm`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: body,
    });

    const data: OrderBookingFormResponse = await response.json();

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to order booking form: ${data.error || "Unknown error from server"}. details: ${errorText}`);
    }

    return data;
  } catch (error) {
    console.error("Error ordering booking form:", error);
    throw error;
  }
};

export const orderBookingFinish = async (params: {
  user: {
    email: string;
    comment: string;
    phone: string;
  };
  supplier_data: {
    first_name_original: string;
    last_name_original: string;
    phone: string;
    email: string;
  };
  partner: {
    partner_order_id: string;
    comment: string;
    amount_sell_b2b2c?: string | null;
  };
  language: string;
  rooms: {
    guests: {
      first_name: string;
      last_name: string;
      age?: number | null;
      is_child?: boolean | null;
      gender?: string | null;
    }[];
  }[];
  upsell_data: {
    name: string;
    uid: string;
  }[];
  payment_type: {
    type: string;
    amount: string;
    currency_code: string;
  };
}): Promise<any> => {
  const url = `${API_BASE_URL}/api/rateHawkRoute/orderBookingFinish`;

  const headers = {
    "Content-Type": "application/json",
  };

  const body = JSON.stringify(params);

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: headers,
      body: body,
    });

    const data = await response.json();

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error finishing order booking: ${data.error || "Unknown error from server"}. Details: ${errorText}`);
    }
    
    return data;
  } catch (error) {
    console.error("Error finishing order booking:", error);
    throw error;
  }
};

export const orderBookingFinishStatus = async (params: {
  partner_order_id: string;
}): Promise<any> => {
  const body = JSON.stringify(params);

  try {
    const response = await fetch(`${API_BASE_URL}/api/rateHawkRoute/orderBookingFinishStatus`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: body,
    });

    const data = await response.json();

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to order booking form: ${data.error || 'Unknow error from server'}. Details: ${errorText}`);
    }

    
    return data;
  } catch (error) {
    console.error("Error ordering booking form fnish:", error);
    throw error;
  }
};

export const orderInformation = async (params: {
  ordering: {
    ordering_type: string;
    ordering_by: string;
  };
  pagination: {
    page_size: string;
    page_number: string;
  };
  search: {
    created_at: {
      from_date: string;
    }
  };
  language: string;
}): Promise<any> => {
  const body = JSON.stringify(params);

  try {
    const response = await fetch (`${API_BASE_URL}/api/rateHawkRoute/orderInformation`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: body,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch order information: ${response.statusText}. Details: ${errorText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching order information:", error);
    throw error;
  }
};

export const orderCancellation = async (params: {
  partner_order_id: string;
}): Promise<any> => {
  const body = JSON.stringify(params);

  try {
    const response = await fetch (`${API_BASE_URL}/api/rateHawkRoute/orderCancellation`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: body,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch order information: ${response.statusText}. Details: ${errorText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error order cancellation:', error);
    throw error;
  }
};

export const saveRateHawkBookingToDb = async (
  userId: string,
  partner_order_id: string,
  book_hash: string,
  tripStartDate: string,
  tripEndDate: string,
  bookedDate: string,
  paidAmount: string,
  createdAt: string,
  updatedAt: string,
  email: string,
  id: string,
  name: string,
  postal_code: string,
  address: string,
  holdOrPaid: string,
  actualAmount: string,
  status: string,
  orderNumber: string,
  primaryGuestName: string,
) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/rateHawkBookingRoute/createBooking`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId: userId,
          partner_order_id: partner_order_id,
          book_hash: book_hash,
          tripStartDate: tripStartDate,
          tripEndDate: tripEndDate,
          bookedDate: bookedDate,
          paidAmount: paidAmount,
          createdAt: createdAt,
          updatedAt: updatedAt,
          email: email,
          id: id,
          name: name,
          postal_code: postal_code,
          address: address,
          holdOrPaid: holdOrPaid,
          actualAmount: actualAmount,
          status: status,
          orderNumber: orderNumber,
          primaryGuestName: primaryGuestName,
        }),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to save booking to database");
    }

    const result = await response.json();
    console.log(result.message);
  } catch (error) {
    console.error("Error saving booking to database:", error);
  }
};


export const fetchBookingByPartnerId = async (partnerId: string): Promise<BookingToTable | null> => {
  try {
      const response = await fetch(`${API_BASE_URL}/api/rateHawkBookingRoute/getBookingByPartnerId?partnerId=${partnerId}`, {
          method: "GET",
          headers: {
              "Content-Type": "application/json",
          },
      });

      if (!response.ok) {
          throw new Error("Failed to fetch booking by partner id");
      }

      const booking = await response.json();
      return booking; 
  } catch (error) {
      console.error("Error fetching booking by partner id:", error);
      return null; 
  }
};

export const fetchUserBooking = async (userId: string, isAdmin: Boolean): Promise<BookingToTable[]> => {
  try {
      const response = await fetch(`${API_BASE_URL}/api/rateHawkBookingRoute/getBookings?userId=${userId}&isAdmin=${isAdmin}`, {
          method: "GET",
          headers: {
              "Content-Type": "application/json",
          },
      });

      if (!response.ok) {
          throw new Error("Failed to fetch bookings");
      }

      const bookings = await response.json();
      //console.log("User Bookings:", bookings);
      return bookings; 
  } catch (error) {
      console.error("Error fetching user bookings:", error);
      return []; 
  }
};


export const updateRatehawkBookingInDb = async (
  bookingDetails: BookingToTable,
  orderNumber: string,
  userId?: string
) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/rateHawkBookingRoute/updateBookingByOrderNumber/${orderNumber}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId: userId,
          partner_order_id: bookingDetails.partner_order_id,
          book_hash: bookingDetails.book_hash,
          tripStartDate: bookingDetails.tripStartDate,
          tripEndDate: bookingDetails.tripEndDate,
          bookedDate: bookingDetails.bookedDate,
          paidAmount: bookingDetails.paidAmount,
          createdAt: bookingDetails.createdAt,
          updatedAt: bookingDetails.updatedAt,
          email: bookingDetails.email,
          id: bookingDetails.id,
          name: bookingDetails.name,
          postal_code: bookingDetails.postal_code,
          address: bookingDetails.address,
          holdOrPaid: bookingDetails.holdOrPaid,
          actualAmount: bookingDetails.actualAmount,
          status: bookingDetails.status,
        }),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to update booking in database");
    }
  } catch (error) {
    console.error("Error updating booking in database:", error);
  }
};

export const updateCancellationStatus = async (orderNumber: string, status: string) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/rateHawkBookingRoute/updateCancellationStatus`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        orderNumber: orderNumber,
        status: status,
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to update cancellation status");
    }

    return true;
  } catch (error) {
    console.error("Error updating cancellation status:", error);
    return false;
  }
};

export const updateRateHawkBookingStatus = async (partner_order_id: string, holdOrPaid: string, paidAmount: string, updatedAt: string) => {
  if (!partner_order_id) return;

  try {
    const response = await fetch(`${API_BASE_URL}/api/rateHawkBookingRoute/updateBookingStatus`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        partner_order_id: partner_order_id,
        holdOrPaid: holdOrPaid,
        paidAmount: paidAmount,
        updatedAt: updatedAt,
      }),
    });

    const data = await response.json();
    if (response.ok) {
      //console.log(`Booking status updated to ${holdOrPaid}`);
    } else {
      console.error("Failed to update booking status:", data.message);
    }
  } catch (error) {
    console.error("Error updating booking status:", error);
  }
};